// ABOUTME: The service desk TopRow metric cards, extracted as standalone presentational components so each can
// ABOUTME: be reused on the service desk page and dropped individually into a BI dashboard cell.
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Text from "../../../../../components/theme/text/Text";
import { tokens } from "../../../../../theme";
import { formatNumber } from "../../../../../utils/helpers";

// All five cards read one `useGetSupportTicketDashboardQuery` payload (passed in raw as `data`; metrics live
// on `data.data`).

// Sub-day durations are unreadable in days ("0.026 days") — show hours
// instead, and sub-hour durations as minutes. The count drives the unit's
// plural form ("1 dag" vs "3 dagen").
const formatDurationDays = (i18n, t, days) => {
    const value = days || 0;
    const hours = value * 24;
    if (hours > 0 && hours < 1) {
        const minutes = Math.round(hours * 60);
        return { value: formatNumber(i18n, minutes), unit: t("minutes", { count: minutes }) };
    }
    if (value > 0 && value < 1) {
        const roundedHours = Math.round(hours);
        return { value: formatNumber(i18n, roundedHours), unit: t("hours", { count: roundedHours }) };
    }
    const roundedDays = Math.round(value);
    return { value: formatNumber(i18n, roundedDays), unit: t("days", { count: roundedDays }) };
};

// New today vs closed today (with the net delta in the header).
export const ServiceTicketDeltaCard = ({ data, isLoading, isFetching, sx }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const metrics = data?.data || {};

    return (
        <Box
            key={"ticket_delta"}
            sx={{
                // flex:2,

                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                p: 1,
                pb: 1.5,
                px: 1.5,
                borderRadius: 5,
                display: 'flex',
                flexDirection: 'column',
                ...sx,
            }}
        >
            <Box
                sx={{
                    p: 1,
                    px: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: 'column'
                    }}
                >
                    <Text>{t("today")}</Text>
                    <Text light>{t("total")}</Text>
                </Box>

                <Typography
                    sx={{
                        lineHeight: "21px",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: 21,
                        fontWeight: "1000",
                        color: colors.txt["primary"]
                    }}
                >
                    {!isLoading && !isFetching ? (metrics?.ticketDelta >= 0 ? '+' : '') + metrics?.ticketDelta : '-'}
                </Typography>

            </Box>
            <Box
                sx={{
                    flex: 1,
                    mt: 1,
                    display: "flex",
                    gap: 1.5,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        borderRadius: 4,
                        px: 2,
                        py: 1.5,
                        minWidth: 120,
                        background: colors.red[100],
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >

                    <Text variant={"red"} semibold>{t("new")}</Text>
                    <Typography
                        sx={{
                            lineHeight: "17px",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 17,
                            fontWeight: "700",
                            color: colors.txt["primary"],
                            mt: 'auto',
                        }}
                    >
                        {!isLoading && !isFetching ? formatNumber(i18n, metrics.newToday || 0) : '-'}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        borderRadius: 4,
                        px: 2,
                        py: 1.5,
                        minWidth: 120,
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,

                    }}
                >

                    <Text light noBreak semibold>{t("closed")}</Text>
                    <Typography
                        sx={{
                            lineHeight: "17px",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 17,
                            fontWeight: "700",
                            color: colors.txt["primary"],
                            mt: 'auto',
                        }}
                    >
                        {!isLoading && !isFetching ? formatNumber(i18n, metrics.closedToday || 0) : '-'}
                    </Typography>
                </Box>

            </Box>
        </Box>
    );
};

// Needs attention: unassigned + stuck >7 days.
export const ServiceUnassignedCard = ({ data, isLoading, isFetching, sx }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const metrics = data?.data || {};

    return (
        <Box
            key={"tickets_unassigned"}
            sx={{
                // flex:2,

                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                p: 1,
                pb: 1.5,
                px: 1.5,
                borderRadius: 5,
                display: 'flex',
                flexDirection: 'column',
                ...sx,
            }}
        >
            <Box
                sx={{
                    p: 1,
                    px: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: 'column'
                    }}
                >
                    <Text>{t("needs_attention")}</Text>
                    <Text light>{t("ticket_unassigned_and_stuck")}</Text>
                </Box>

                {/* <Typography
                    sx={{
                        lineHeight:"21px",
                        fontFamily:"Roboto, sans-serif",
                        fontSize:21,
                        fontWeight:"1000",
                        color:colors.txt["primary"]
                    }}
                >
                    {!isLoading && !isFetching ? formatNumber(i18n, metrics.unassignedCount || 0) : '-'}
                </Typography> */}

            </Box>
            <Box
                sx={{
                    flex: 1,
                    mt: 1,
                    display: "flex",
                    gap: 1.5,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        borderRadius: 4,
                        px: 2,
                        py: 1.5,
                        background: colors.red[100],
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >

                    <Text variant={"red"} semibold>{t("ticket_unassigned")}</Text>
                    <Typography
                        sx={{
                            lineHeight: "17px",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 17,
                            fontWeight: "700",
                            color: colors.txt["primary"],
                            mt: 'auto',
                        }}
                    >
                        {!isLoading && !isFetching ? formatNumber(i18n, metrics.unassignedCount || 0) : '-'}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        borderRadius: 4,
                        px: 2,
                        py: 1.5,
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,

                    }}
                >

                    <Text light noBreak semibold>{t("stuck_more_than_7_days")}</Text>
                    <Typography
                        sx={{
                            lineHeight: "17px",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 17,
                            fontWeight: "700",
                            color: colors.txt["primary"],
                            mt: 'auto',
                        }}
                    >
                        {!isLoading && !isFetching ? formatNumber(i18n, metrics.stuckCount || 0) : '-'}
                    </Typography>
                </Box>

            </Box>
        </Box>
    );
};

// Resolved with reply — needs review.
export const ServiceNeedsReviewCard = ({ data, isLoading, isFetching, sx }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const metrics = data?.data || {};

    return (
        <Box
            key={"tickets_needs_review"}
            sx={{
                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                p: 1,
                pb: 1.5,
                px: 1.5,
                borderRadius: 5,
                display: 'flex',
                flexDirection: 'column',
                ...sx,
            }}
        >
            <Box
                sx={{
                    p: 1,
                    px: 1.5,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Text>{t("ticket_needs_review")}</Text>
                <Text light>{t("ticket_needs_review_subtitle")}</Text>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    mt: 1,
                    display: "flex",
                    gap: 1.5,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        borderRadius: 4,
                        px: 2,
                        py: 1.5,
                        background: colors.orange[100],
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Text variant="orange" semibold>{t("resolved_with_reply")}</Text>
                    <Typography
                        sx={{
                            lineHeight: "17px",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 17,
                            fontWeight: "700",
                            color: colors.txt["primary"],
                            mt: 'auto',
                        }}
                    >
                        {!isLoading && !isFetching ? formatNumber(i18n, metrics.needsReview || 0) : '-'}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

// Length of stay: average + 90th percentile (in days).
export const ServiceLengthOfStayCard = ({ data, isLoading, isFetching, sx }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const metrics = data?.data || {};
    const avgDuration = formatDurationDays(i18n, t, metrics.avgDays);
    const p90Duration = formatDurationDays(i18n, t, metrics.p90Days);

    return (
        <Box
            key={"length_of_stay"}
            sx={{
                // flex:2,

                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                p: 1,
                pb: 1.5,
                px: 1.5,
                borderRadius: 5,
                display: 'flex',
                flexDirection: 'column',
                ...sx,
            }}
        >
            <Box
                sx={{
                    p: 1,
                    px: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: 'column'
                    }}
                >
                    <Text>{t("length_of_stay")}</Text>
                    <Text light>{t("length_of_stay_subtitle")}</Text>
                </Box>


            </Box>
            <Box
                sx={{
                    flex: 1,
                    mt: 1,
                    display: "flex",
                    gap: 1.5,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        borderRadius: 4,
                        px: 2,
                        py: 1.5,
                        background: colors.green[100],
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        pr: 5,
                    }}
                >

                    <Text variant={"green"} semibold>{t("average")}</Text>
                    <Box
                        sx={{
                            display: "flex",
                            mt: 'auto',
                            gap: 0.75,
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            sx={{
                                lineHeight: "17px",
                                fontFamily: "Roboto, sans-serif",
                                fontSize: 17,
                                fontWeight: "700",
                                color: colors.txt["primary"],
                            }}
                        >
                            {!isLoading && !isFetching ? avgDuration.value : '-'}
                        </Typography>

                        <Typography
                            sx={{
                                lineHeight: "21px",
                                fontFamily: "Roboto, sans-serif",
                                fontSize: 17,
                                fontWeight: "1000",
                                color: colors.txt["secondary"]
                            }}
                        >
                            {avgDuration.unit}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        borderRadius: 4,
                        px: 2,
                        py: 1.5,
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,

                    }}
                >

                    <Text light noBreak semibold>{t("90th_percentile")}</Text>


                    <Box
                        sx={{
                            display: "flex",
                            mt: 'auto',
                            gap: 0.75,
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            sx={{
                                lineHeight: "17px",
                                fontFamily: "Roboto, sans-serif",
                                fontSize: 17,
                                fontWeight: "700",
                                color: colors.txt["primary"],
                            }}
                        >
                            {!isLoading && !isFetching ? p90Duration.value : '-'}
                        </Typography>

                        <Typography
                            sx={{
                                lineHeight: "21px",
                                fontFamily: "Roboto, sans-serif",
                                fontSize: 17,
                                fontWeight: "1000",
                                color: colors.txt["secondary"]
                            }}
                        >
                            {p90Duration.unit}
                        </Typography>
                    </Box>
                </Box>

            </Box>
        </Box>
    );
};

// Responsibility split: internal (warehouse) vs relation (customer).
export const ServiceResponsibilityCard = ({ data, isLoading, isFetching, sx }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const metrics = data?.data || {};

    return (
        <Box
            key={"tickets_responsibility"}
            sx={{
                // flex:2,

                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                p: 1,
                pb: 1.5,
                px: 1.5,
                borderRadius: 5,
                display: 'flex',
                flexDirection: 'column',
                ...sx,
            }}
        >
            <Box
                sx={{
                    p: 1,
                    px: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: 'column'
                    }}
                >
                    <Text>{t("ticket_responsibility")}</Text>
                    <Text light>{t("ticket_responsibility_subtitle")}</Text>
                </Box>

            </Box>
            <Box
                sx={{
                    flex: 1,
                    mt: 1,
                    display: "flex",
                    gap: 1.5,
                }}
            >

                <Box
                    sx={{
                        flex: 1,
                        borderRadius: 4,
                        px: 2,
                        py: 1.5,
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,

                    }}
                >

                    <Text light noBreak semibold>{t("internal")}</Text>
                    <Typography
                        sx={{
                            lineHeight: "17px",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 17,
                            fontWeight: "700",
                            color: colors.txt["primary"],
                            mt: 'auto',
                        }}
                    >
                        {!isLoading && !isFetching ? formatNumber(i18n, metrics.responsibility?.warehouse || 0) : '-'}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        borderRadius: 4,
                        px: 2,
                        py: 1.5,
                        background: colors.green[100],
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        minWidth: 200,
                    }}
                >

                    <Text variant={"green"} semibold>{t("relation")}</Text>
                    <Typography
                        sx={{
                            lineHeight: "17px",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 17,
                            fontWeight: "700",
                            color: colors.txt["primary"],
                            mt: 'auto',
                        }}
                    >
                        {!isLoading && !isFetching ? formatNumber(i18n, metrics.responsibility?.customer || 0) : '-'}
                    </Typography>
                </Box>


            </Box>
        </Box>
    );
};
